import { formatDate, formatNumber, type AppLocale } from "../i18n/format";
import { arabicDayNoun, ageWord, type DataAge } from "../rates/age";

/**
 * The data-age gauge, and the replacement for the original countdown ring.
 *
 * FORM AND WHY: a 270-degree arc that FILLS clockwise as the rates age, paired
 * with the real quote date in words. It reads as an instrument gauge — which
 * suits a product whose references are rate boards and printed tables — and,
 * unlike a countdown, it makes no promise about a future event. A draining ring
 * says "something is about to happen"; a filling arc says "this is how old this
 * is", which is the only claim the data actually supports.
 *
 * Nothing is printed inside the arc. The numerals belong to the amounts, and
 * spending mono digits on chrome would compete with them.
 *
 * The arc turns --warn past 24 hours or whenever we are offline, so staleness
 * is legible at a glance without any copy.
 */

const SIZE = 18;
const RADIUS = 7;
const SWEEP = 0.75; // 270 degrees, leaving a gap at the bottom like a dial
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC_LENGTH = CIRCUMFERENCE * SWEEP;

interface Props {
  readonly age: DataAge;
  readonly locale: AppLocale;
}

function ageLabel(age: DataAge, locale: AppLocale): string {
  if (age.date === null) {
    return locale === "ar" ? "لا توجد بيانات" : "no data";
  }
  if (age.state === "offline") {
    return locale === "ar" ? "دون اتصال" : "offline";
  }
  const word = ageWord(age.days, locale);
  if (word !== null) return word;

  const count = formatNumber(age.days, locale, { maximumFractionDigits: 0 });
  // Arabic day-noun agreement depends on the count; see arabicDayNoun.
  return locale === "ar"
    ? `منذ ${count} ${arabicDayNoun(age.days)}`
    : `${count} days old`;
}

export function DataAgeIndicator({ age, locale }: Props) {
  const dateText =
    age.date === null
      ? locale === "ar"
        ? "غير معروف"
        : "unknown"
      : formatDate(age.date, locale, { day: "2-digit", month: "short", year: "numeric" });

  const label = ageLabel(age, locale);

  const accessible =
    locale === "ar"
      ? `الأسعار بتاريخ ${dateText}، ${label}`
      : `Rates as of ${dateText}, ${label}`;

  return (
    <div className="data-age" data-state={age.state}>
      <svg
        className="data-age__gauge"
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden="true"
        focusable="false"
      >
        <circle
          className="data-age__track"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE}`}
        />
        <circle
          className="data-age__fill"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeDasharray={`${ARC_LENGTH * age.fraction} ${CIRCUMFERENCE}`}
        />
      </svg>

      <span className="data-age__text">
        <span className="data-age__label">
          {locale === "ar" ? "الأسعار بتاريخ" : "Rates as of"}
        </span>{" "}
        {/*
         * <time>, and dir="auto" rather than "ltr" — the THIRD surface that
         * renders a date.
         *
         * Found by enumerating every caller of formatDate rather than by
         * noticing it: the row's stale marker and the sheet's "As of" were
         * fixed first, and this one would have kept laying out "09 2026 أغسطس"
         * indefinitely. A bidi rule is a property of the string, so it holds
         * everywhere the string appears or it does not hold.
         *
         * The element is <time> because a date should say so — it hands the
         * machine-readable value to assistive tech, and it gives
         * check-date-order.mjs something unambiguous to select. Sniffing text
         * for "a number that looks like a year" read 0.9793 as one.
         */}
        <time className="data-age__date" dir="auto" dateTime={age.date ?? undefined}>
          {dateText}
        </time>
        <span className="data-age__sep" aria-hidden="true">
          {" · "}
        </span>
        <span className="data-age__word">{label}</span>
      </span>

      {/* One clean sentence for assistive tech instead of the fragmented spans. */}
      <span className="visually-hidden">{accessible}</span>
    </div>
  );
}
